/**
 * "No code?" under a code entry (sign-in, step-up, a secure link, a new
 * contact address): a small popup that says, first and plainly, to check
 * the junk or spam folder, and holds "Send a new code" with its countdown.
 * Plain elements, no React Aria, so the public pages that use it stay light.
 * It closes on Escape, on a click outside, and after sending.
 *
 * @example
 * <NoCodeHelp wait={wait} pending={sending} onResend={() => void resend()} />
 */
import { MailWarning, MessageSquareWarning, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './no-code.css';

/** Props for `NoCodeHelp`. */
export interface NoCodeHelpProps {
  /** Seconds until a new code may be sent; 0 when it may be now. */
  wait: number;
  /** Sends a new code. */
  onResend: () => void;
  /** While a code is being sent. */
  pending?: boolean;
  /** Where the code went: the advice differs for a text message. */
  channel?: 'email' | 'sms';
  className?: string;
}

/** "0:42", or "1:05". */
const clock = (seconds: number) =>
  `${String(Math.floor(seconds / 60))}:${String(seconds % 60).padStart(2, '0')}`;

/** The trigger and its popup. */
export function NoCodeHelp({
  wait,
  onResend,
  pending = false,
  channel = 'email',
  className,
}: NoCodeHelpProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    const onPointer = (event: PointerEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  const Icon = channel === 'sms' ? MessageSquareWarning : MailWarning;
  return (
    <div ref={root} className={`no-code ${className ?? ''}`}>
      <button
        ref={trigger}
        type="button"
        className="no-code__trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {t('ui.noCode.trigger')}
      </button>
      <div
        id={panelId}
        className="no-code__panel"
        role="region"
        aria-labelledby={titleId}
        hidden={!open}
      >
        <button
          type="button"
          className="no-code__close"
          aria-label={t('ui.noCode.close')}
          onClick={() => {
            setOpen(false);
            trigger.current?.focus();
          }}
        >
          <X aria-hidden="true" />
        </button>
        <span className="no-code__icon" aria-hidden="true">
          <Icon />
        </span>
        <p className="no-code__title" id={titleId}>
          {t(channel === 'sms' ? 'ui.noCode.smsTitle' : 'ui.noCode.title')}
        </p>
        <p className="no-code__body">
          {t(channel === 'sms' ? 'ui.noCode.smsBody' : 'ui.noCode.body')}
        </p>
        <button
          type="button"
          className="btn btn--secondary btn--sm no-code__resend"
          disabled={wait > 0 || pending}
          onClick={() => {
            onResend();
            setOpen(false);
          }}
        >
          {wait > 0 ? t('ui.noCode.resendIn', { time: clock(wait) }) : t('ui.noCode.resend')}
        </button>
      </div>
    </div>
  );
}
